/**
 * plugins/storage/isDoctorCache.js
 *
 * Short-TTL cache for the isDoctor / getAuthStatus result so route
 * navigation does not round-trip to Parse on every click.
 *
 * Mirrored to sessionStorage so the router guard answers synchronously on
 * the first nav after a hard refresh while the live call revalidates in the
 * background. sessionStorage rather than localStorage keeps it per tab: a
 * server-side role change lands after TTL_MS, and a new tab starts fresh.
 * Entries also revalidate on window focus; on a miss the caller falls back
 * to the live Parse.Cloud.run call.
 */
const TTL_MS = 60_000;

const IS_DOCTOR_KEY = 'esteps-cache:isDoctor';
const AUTH_STATUS_KEY = 'esteps-cache:authStatus';

function readSession(key) {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.at !== 'number') return null;
    if (Date.now() - parsed.at > TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSession(key, entry) {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // QuotaExceeded under strict storage policies - fall through to
    // in-memory cache only; the next nav refetches.
  }
}

function clearSession(key) {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Ignore - best-effort cleanup.
  }
}

// In-memory mirror so reads inside one render pass don't keep hitting
// sessionStorage. Hydrated lazily from sessionStorage on first read.
let cache = null;
let authStatusCache = null;

export function getCachedIsDoctor() {
  if (cache) {
    if (Date.now() - cache.at > TTL_MS) {
      cache = null;
    } else {
      return cache.value;
    }
  }
  const persisted = readSession(IS_DOCTOR_KEY);
  if (!persisted) return null;
  cache = persisted;
  return persisted.value;
}

export function setCachedIsDoctor(value) {
  const entry = { value, at: Date.now() };
  cache = entry;
  writeSession(IS_DOCTOR_KEY, entry);
}

export function getCachedAuthStatus() {
  if (authStatusCache) {
    if (Date.now() - authStatusCache.at > TTL_MS) {
      authStatusCache = null;
    } else {
      return authStatusCache.value;
    }
  }
  const persisted = readSession(AUTH_STATUS_KEY);
  if (!persisted) return null;
  authStatusCache = persisted;
  return persisted.value;
}

export function setCachedAuthStatus(value) {
  const entry = { value, at: Date.now() };
  authStatusCache = entry;
  writeSession(AUTH_STATUS_KEY, entry);
}

export function invalidateIsDoctor() {
  cache = null;
  authStatusCache = null;
  clearSession(IS_DOCTOR_KEY);
  clearSession(AUTH_STATUS_KEY);
}

if (typeof window !== 'undefined') {
  // Both events still drop the cache. We keep this behavior so a
  // backend-side role change is reflected on the next nav after the
  // tab regains attention.
  window.addEventListener('focus', invalidateIsDoctor);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') invalidateIsDoctor();
  });
}
